import { useEffect } from 'react';
import type { MapEntityPath, PlacedSymbol, LayerVisibility } from './types';
import { BUFFER_SIZE } from './types';
import { renderPaths, renderInProgressPath } from './path-renderer';

interface PathCanvasInput {
    canvasRef: React.RefObject<HTMLCanvasElement | null>;
    mapEntityPaths: MapEntityPath[];
    inProgressPathVertices: { x: number; y: number }[];
    pathCursorVertex: { x: number; y: number } | null;
    isDrawingPath: boolean;
    dimensions: { width: number; height: number };
    zoomRef: React.MutableRefObject<number>;
    panRef: React.MutableRefObject<{ x: number; y: number }>;
    selectedPathUid: string | null;
    hoveredPathUid: string | null;
    isCustomUpload: boolean;
    customMapImageRef?: React.RefObject<HTMLImageElement | null>;
    cameraTick: number;
    placedSymbols: PlacedSymbol[];
    layerVisibility: LayerVisibility;
    activeSection: string;
}

/**
 * Side-effect hook: renders all placed paths and the in-progress path
 * onto the dedicated path canvas.  Extracted from map-canvas.tsx.
 */
export function usePathCanvas({
    canvasRef,
    mapEntityPaths,
    inProgressPathVertices,
    pathCursorVertex,
    isDrawingPath,
    dimensions,
    zoomRef,
    panRef,
    selectedPathUid,
    hoveredPathUid,
    isCustomUpload,
    customMapImageRef,
    cameraTick,
    placedSymbols,
    layerVisibility,
    activeSection,
}: PathCanvasInput): void {
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const { width, height } = dimensions;
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.clearRect(0, 0, width, height);

        const isCustom = isCustomUpload && !!customMapImageRef?.current;
        const mW = isCustom ? customMapImageRef!.current!.naturalWidth : BUFFER_SIZE;
        const mH = isCustom ? customMapImageRef!.current!.naturalHeight : BUFFER_SIZE;

        let rW = Math.min(width, height);
        let rH = Math.min(width, height);
        if (isCustom) {
            const ratio = mW / mH;
            if (width / height > ratio) { rH = height; rW = height * ratio; }
            else { rW = width; rH = width / ratio; }
        }

        const zoom = zoomRef.current;
        const pan = panRef.current;

        const toScreen = (x: number, y: number) => ({
            sx: (x / mW) * rW * zoom + pan.x,
            sy: (y / mH) * rH * zoom + pan.y,
        });

        const visiblePaths = mapEntityPaths.filter(p => {
            if (activeSection === 'paths') return true;
            if (p.pathType === 'river') return layerVisibility.rivers;
            return layerVisibility.paths;
        });

        renderPaths(ctx, visiblePaths, toScreen, {
            selectedUid: selectedPathUid ?? null,
            hoveredUid: hoveredPathUid,
            zoom,
            placedSymbols,
        });

        renderInProgressPath(ctx, inProgressPathVertices, pathCursorVertex, toScreen, zoom);
    }, [
        canvasRef, mapEntityPaths, inProgressPathVertices, pathCursorVertex, isDrawingPath,
        dimensions, zoomRef, panRef, selectedPathUid, hoveredPathUid,
        isCustomUpload, customMapImageRef, cameraTick, placedSymbols,
        layerVisibility, activeSection,
    ]);
}
